import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // Keyboard support (WCAG 2.1.1): submenus no longer expand on focus — they open
    // with ArrowDown / Enter and close with Escape or when focus leaves the item.
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('click', this.#onDocumentClick);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    /**
     * True when the active item was opened by pointerenter. The click that
     * belongs to the same gesture (mouse click after hover, the tap that fired
     * pointerenter+click on touch) must not close what its own hover opened.
     */
    activatedByPointer: false,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * The "More" overflow trigger button. It lives in the light DOM under
   * [slot="more"], so findMenuItem never resolves to it (that helper redirects
   * to the first overflow item) — aria-expanded has to be synced here directly.
   */
  get moreTrigger() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[slot="more"] [aria-haspopup="true"]'));
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Close when clicking outside the menu (click-to-open no longer uses pointerleave).
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Toggle a submenu from its trigger (on:click="/toggle").
   * Parent links preventDefault so click opens the dropdown instead of navigating.
   * @param {MouseEvent | { target: EventTarget | null, preventDefault?: () => void }} event
   */
  toggle = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const listItem = target?.closest('.menu-list__list-item');
    if (!listItem) return;

    if (target?.closest('a[aria-haspopup="true"]')) {
      event.preventDefault?.();
    }

    const item = findMenuItem(listItem);

    if (item && item === this.#state.activeItem) {
      if (this.#state.activatedByPointer) {
        // pointerenter already opened this submenu as part of the same gesture;
        // the first click keeps it open, the next one closes it.
        this.#state.activatedByPointer = false;
        return;
      }
      this.#deactivate(item, { force: true });
    } else {
      this.activate({ target: listItem });
    }
  };

  /**
   * Escape closes the open submenu and returns focus to its trigger; ArrowDown on a
   * top-level item with a submenu opens it and moves focus to the first link
   * (disclosure-navigation pattern — submenus never open on focus alone).
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      const active = this.#state.activeItem;
      if (!active) return;

      event.preventDefault();
      this.#deactivate(active, { force: true });
      // An active overflow item is hidden once the menu collapses — return
      // focus to the visible "More" trigger instead of the vanished item.
      const focusTarget = active instanceof Element && active.closest('[slot="overflow"]') ? this.moreTrigger : active;
      if (focusTarget instanceof HTMLElement) focusTarget.focus();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      // Only reachable when `disable_parent_click` is off, which leaves parents as
      // anchors. Held keys auto-repeat keydown, and each repeat would re-toggle the
      // panel — guard so one press is one activation. Buttons never land here: they
      // fire a native click from Enter/Space straight into on:click="/toggle".
      if (event.repeat) return;

      const trigger = event.target instanceof Element ? event.target.closest('a[aria-haspopup="true"]') : null;
      if (trigger) {
        event.preventDefault();
        this.toggle({ target: trigger });
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      // [aria-haspopup] rather than [ref="menuitem"] so the "More" overflow
      // trigger participates in the disclosure model too.
      const trigger = event.target instanceof Element ? event.target.closest('[aria-haspopup="true"]') : null;
      if (!trigger) return;

      event.preventDefault();
      const listItem = trigger.closest('.menu-list__list-item');

      if (listItem && this.#state.activeItem !== findMenuItem(listItem)) {
        this.activate({ target: listItem });
      }

      const firstLink = trigger.closest('[slot="more"]')
        ? this.refs.overflowMenu?.querySelector('[slot="overflow"] a[href], [slot="overflow"] button:not([disabled])')
        : findSubmenu(trigger)?.querySelector('a[href], button:not([disabled])');
      if (firstLink instanceof HTMLElement) firstLink.focus();
    }
  };

  /**
   * When keyboard focus leaves an open item (Tab past the last submenu link, or
   * Shift+Tab out of the trigger), collapse it so the expanded submenu never
   * lingers detached from focus. Pointer-opened submenus are unaffected: this
   * only acts when the departing focus was inside the active item.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const active = this.#state.activeItem;
    if (!active) return;

    // When the overflow menu is open, activeItem is the FIRST overflow item —
    // scoping to its list item would collapse the menu while tabbing between
    // overflow links. The focus boundary there is the whole overflow-list
    // (slotted items stay its light-DOM children, so contains() sees them).
    const scope = active.closest('[slot="overflow"]') ? this.refs.overflowMenu : active.closest('.menu-list__list-item');
    if (!scope) return;
    if (!(event.target instanceof Node) || !scope.contains(event.target)) return;

    const next = event.relatedTarget;
    if (next instanceof Node && scope.contains(next)) return;

    this.#deactivate(active, { force: true });
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | { target: EventTarget | null }} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activatedByPointer = 'type' in event && event.type === 'pointerenter';
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    this.moreTrigger?.setAttribute('aria-expanded', (!isDefaultSlot).toString());

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Wait a frame so hydrated submenu content is laid out before measuring
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (this.hasAttribute('data-compact-submenu') && isDefaultSlot) {
      finalHeight = 0;
    }

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options] Explicit close (click / keyboard) ignores the overflow :hover guard.
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // pointerleave on [part="overflow"] can fire while slotted overflow items still
    // match :hover — keep the panel open for that transition. Click-to-close must
    // not wait: the pointer is over the overflow list when toggling an already-open
    // overflow parent, and preventDefault already blocked navigation.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activatedByPointer = false;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    this.moreTrigger?.setAttribute('aria-expanded', 'false');
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
